import { GoogleGenAI, Type } from "@google/genai";
import { CreativeBrief, CampaignConcept } from "../types";

const getAI = () => {
  // Allow manual key override for Netlify deployments
  const manualKey = typeof window !== 'undefined' ? localStorage.getItem('gemini_api_key') : null;
  
  // Statically access process.env.API_KEY so the platform bundler can replace it
  const envKey = process.env.API_KEY || process.env.GEMINI_API_KEY;
  
  const apiKey = manualKey || envKey;
  
  if (!apiKey) {
    throw new Error("API Key is missing. Please connect a valid key.");
  }
  
  return new GoogleGenAI({ apiKey });
};

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const synthesizePrompt = (brief: CreativeBrief): string => {
  const anatomicalParts = brief.gender === 'female' 
    ? `Fit: ${brief.bustLine}, ${brief.waistRatio}, ${brief.hipsSculpt}.`
    : `Physique: ${brief.waistRatio}, ${brief.hipsSculpt}.`;

  const segments = [
    `Subject: ${brief.gender}.`,
    `Vibe: ${brief.vibe}.`,
    `Location: ${brief.scene}.`,
    `Wardrobe: ${brief.outfit}.`,
    `Lighting: ${brief.lighting}.`,
    `Optics: ${brief.lens}.`,
    `Realism: ${brief.realismTuning}.`,
    brief.hairStyle !== "As in photo" ? `Hair: ${brief.hairStyle}.` : "",
    `Skin: ${brief.skinStyle}.`,
    anatomicalParts,
    `Angle: ${brief.cameraAngle}.`,
    brief.customPrompt ? `Details: ${brief.customPrompt}.` : ""
  ].filter(Boolean);

  return segments.join(" ");
};

export const refineBriefPrompt = async (currentPrompt: string): Promise<string> => {
  const ai = getAI();
  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `Transform this simple fashion prompt into a highly technical, cinematic, and professional editorial directive: "${currentPrompt}". Focus on fabric textures, light interaction, and technical photography terms. Keep it under 50 words.`,
    });
    return response.text?.trim() || currentPrompt;
  } catch (error) {
    console.error("Refinement failed", error);
    return currentPrompt;
  }
};

export const generateCampaignConcepts = async (brief: CreativeBrief): Promise<CampaignConcept[]> => {
  const ai = getAI();
  const synthPrompt = synthesizePrompt(brief);

  const systemInstruction = `YOU ARE A HIGH-FASHION STUDIO CREATIVE DIRECTOR. 
  Storyboard 1 campaign theme for a ${brief.gender} subject.
  FOCUS ON: Lighting, environment, high-fashion pose, and specific textile details.
  STRICT RULE: Do NOT describe the subject's facial features. The identity is provided by the anchor images.
  Output JSON ARRAY ONLY with 1 item.`;

  // Only use text for concept generation to save tokens/bandwidth
  const parts: any[] = [{ text: `BASE DIRECTIVE: ${synthPrompt}` }];

  if (brief.customLocationImage) {
    const mime = brief.customLocationImage.split(';')[0].split(':')[1] || 'image/jpeg';
    parts.push({ inlineData: { mimeType: mime, data: brief.customLocationImage.split(',')[1] } });
    parts.push({ text: "Use the provided custom location image as the primary inspiration for the scene environment." });
  }

  if (brief.customAttireImage) {
    const mime = brief.customAttireImage.split(';')[0].split(':')[1] || 'image/jpeg';
    parts.push({ inlineData: { mimeType: mime, data: brief.customAttireImage.split(',')[1] } });
    parts.push({ text: "Use the provided custom attire image as the primary inspiration for the wardrobe." });
  }

  const attemptConceptGeneration = async (retries: number): Promise<CampaignConcept[]> => {
    try {
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: { parts: parts.map(p => ({ ...p })) }, // deep copy parts
        config: {
          systemInstruction,
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                id: { type: Type.STRING },
                themeIndex: { type: Type.NUMBER },
                title: { type: Type.STRING },
                description: { type: Type.STRING },
                prompt: { type: Type.STRING },
              },
              required: ["id", "themeIndex", "title", "description", "prompt"],
            },
          },
        },
      });

      return JSON.parse(response.text?.trim() || "[]");
    } catch (error: any) {
      if (retries > 0 && !error.message?.includes("API key not valid") && !error.message?.includes("403")) {
        console.warn(`Concept generation failed. Retrying... (${retries} left)`, error);
        await new Promise(r => setTimeout(r, 2000));
        return attemptConceptGeneration(retries - 1);
      }
      throw error;
    }
  };

  try {
    return await attemptConceptGeneration(3);
  } catch (error: any) {
    console.error("Concept Generation Error", error);
    if (error.message?.includes("API key not valid") || error.message?.includes("403")) {
      throw new Error("KEY_RESET_REQUIRED");
    }
    throw new Error(`Failed to generate concepts: ${error.message}`);
  }
};

export const generateTwinImage = async (
  brief: CreativeBrief,
  conceptPrompt: string
): Promise<string> => {
  const ai = getAI();
  
  const parts: any[] = [];
  const activeAnchors = Object.values(brief.anchorImages).filter(img => img !== null);
  
  // Add anchor images for reference
  activeAnchors.forEach((img) => {
    const mime = img!.split(';')[0].split(':')[1] || 'image/jpeg';
    parts.push({ inlineData: { mimeType: mime, data: img!.split(',')[1] } });
  });

  if (brief.customLocationImage) {
    const mime = brief.customLocationImage.split(';')[0].split(':')[1] || 'image/jpeg';
    parts.push({ inlineData: { mimeType: mime, data: brief.customLocationImage.split(',')[1] } });
  }

  if (brief.customAttireImage) {
    const mime = brief.customAttireImage.split(';')[0].split(':')[1] || 'image/jpeg';
    parts.push({ inlineData: { mimeType: mime, data: brief.customAttireImage.split(',')[1] } });
  }

  // Simplified prompt to avoid safety triggers while maintaining quality
  const finalPrompt = `
    High-fashion editorial photography.
    SUBJECT IDENTITY (CRITICAL): You must clone the exact facial features, eye shape, nose structure, jawline, and proportions of the person in the reference photos. Retain their exact likeness and identity. Do NOT alter, over-smooth, or artificially beautify the core facial geometry.
    SCENE: ${conceptPrompt}. ${brief.customLocationImage ? 'Also use the provided location reference image to guide the environment.' : ''}
    ${brief.customAttireImage ? 'WARDROBE: Use the provided attire reference image to guide the outfit.' : ''}
    TECHNICAL: ${brief.lighting}, ${brief.lens}, ${brief.cameraAngle}.
    AESTHETIC: ${brief.realismTuning}, ${brief.skinStyle}, ${brief.bodyType}.
    QUALITY: Masterpiece, 8k, highly detailed, photorealistic.
  `.trim();

  parts.push({ text: finalPrompt });

  const attemptGeneration = async (model: string, isFallback: boolean) => {
    let attempts = 0;
    const maxAttempts = 3;
    
    while (attempts < maxAttempts) {
      try {
        const config: any = {
          imageConfig: {
            aspectRatio: brief.aspectRatio,
          }
        };

        // Only add imageSize for Pro model (supported feature)
        // Flash models do not support imageSize
        if (!isFallback && brief.imageSize) {
           config.imageConfig.imageSize = brief.imageSize;
        }

        // Create a deep copy of contents for each attempt to avoid stream/body locking issues
        const freshParts = parts.map(p => ({ ...p, inlineData: p.inlineData ? { ...p.inlineData } : undefined, text: p.text }));

        const response = await ai.models.generateContent({
          model,
          contents: { parts: freshParts },
          config,
        });

        const imagePart = response.candidates?.[0]?.content?.parts?.find(p => !!p.inlineData);
        
        if (imagePart?.inlineData) {
          return `data:image/png;base64,${imagePart.inlineData.data}`;
        }

        const candidate = response.candidates?.[0];
        if (candidate?.finishReason) {
          if (candidate.finishReason === 'SAFETY') throw new Error("Safety Block: Prompt triggered filters");
          if (candidate.finishReason === 'RECITATION') throw new Error("Copyright Block");
          throw new Error(`Generation stopped: ${candidate.finishReason}`);
        }

        throw new Error("No image data returned");

      } catch (error: any) {
        console.warn(`[${model}] Attempt ${attempts + 1} failed:`, error.message);
        
        const isRetryable = error.message?.includes("503") || 
                            error.message?.includes("429") || 
                            error.message?.includes("Overloaded") ||
                            error.status === 503;

        // Terminal errors
        if (error.message?.includes("403") || error.message?.includes("API key")) throw new Error("KEY_RESET_REQUIRED");

        if (isRetryable && attempts < maxAttempts - 1) {
          attempts++;
          const delay = (attempts + 1) * 2000; // 2s, 4s, 6s
          console.log(`[${model}] Retrying in ${delay}ms...`);
          await wait(delay);
          continue;
        }
        
        throw error;
      }
    }
    throw new Error(`Failed to generate with ${model} after retries`);
  };

  try {
    // Try Primary Model (Pro)
    return await attemptGeneration('gemini-3-pro-image-preview', false);
  } catch (error: any) {
    // Check if we should fallback
    const isOverload = error.message?.includes("503") || error.message?.includes("429") || error.message?.includes("Overloaded");
    const isNotFound = error.message?.includes("404") || error.message?.includes("not found");
    
    if (isOverload || isNotFound) {
      console.warn(`Primary model failed (${isNotFound ? 'Not Found' : 'Overloaded'}). Falling back to flash model...`);
      try {
        // Fallback to Flash Model
        return await attemptGeneration('gemini-2.5-flash-image', true);
      } catch (fallbackError: any) {
        if (fallbackError.message === "KEY_RESET_REQUIRED") throw fallbackError;
        throw new Error("High Demand: All models are currently busy. Please try again later.");
      }
    }
    
    throw error;
  }
};